import React, {useEffect, useState} from "react";
import axios from "axios";
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Grid, IconButton, Modal, Paper, TextField, Tooltip, Typography
} from "@mui/material";
import {DataGrid, GridActionsCellItem, GridColDef} from "@mui/x-data-grid";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BedIcon from "@mui/icons-material/Bed";
import MapIcon from "@mui/icons-material/Map";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {EventSite, SleepingAssignment} from "../../../server/Plot";

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const assignmentModalStyle = {
    ...modalStyle,
    width: 440,
};

function emptyAssignment(): Omit<SleepingAssignment, 'id' | 'eventSiteId'> {
    return {name: '', bunks: 0, power: false, medical: false, accessible: false, npcOnly: false, staffOnly: false};
}

export default function EventSiteManagement() {
    const [sites, setSites] = useState<EventSite[]>([]);
    const mapFileInputRef = React.useRef<HTMLInputElement>(null);
    const [mapUploadSiteId, setMapUploadSiteId] = useState<number | null>(null);
    const [siteModalOpen, setSiteModalOpen] = useState(false);
    const [editingSite, setEditingSite] = useState<EventSite | null>(null);
    const [siteName, setSiteName] = useState('');
    const [siteAddress, setSiteAddress] = useState('');
    const [deleteSiteTarget, setDeleteSiteTarget] = useState<EventSite | null>(null);

    const [managingSite, setManagingSite] = useState<EventSite | null>(null);
    const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState<SleepingAssignment | null>(null);
    const [assignment, setAssignment] = useState(emptyAssignment());
    const [deleteAssignmentTarget, setDeleteAssignmentTarget] = useState<SleepingAssignment | null>(null);

    useEffect(() => {
        loadSites();
    }, []);

    function loadSites() {
        axios.get('/admin/sites').then(({data}) => setSites(Array.isArray(data) ? data : []));
    }

    function openMapUpload(siteId: number) {
        setMapUploadSiteId(siteId);
        mapFileInputRef.current?.click();
    }

    async function handleMapFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file || mapUploadSiteId == null) return;
        const formData = new FormData();
        formData.append('map', file);
        await axios.post(`/admin/sites/${mapUploadSiteId}/map`, formData, {headers: {'Content-Type': 'multipart/form-data'}});
        setSites(prev => prev.map(s => s.id === mapUploadSiteId ? {...s, siteMapKey: `site-maps/${mapUploadSiteId}`} : s));
        e.target.value = '';
        setMapUploadSiteId(null);
    }

    async function deleteMap(site: EventSite) {
        await axios.delete(`/admin/sites/${site.id}/map`);
        setSites(prev => prev.map(s => s.id === site.id ? {...s, siteMapKey: null} : s));
    }

    function openCreateSite() {
        setEditingSite(null);
        setSiteName('');
        setSiteAddress('');
        setSiteModalOpen(true);
    }

    function openEditSite(site: EventSite) {
        setEditingSite(site);
        setSiteName(site.name);
        setSiteAddress(site.address ?? '');
        setSiteModalOpen(true);
    }

    function closeSiteModal() {
        setSiteModalOpen(false);
        setEditingSite(null);
        setSiteName('');
        setSiteAddress('');
    }

    async function saveSite() {
        const payload = {name: siteName, address: siteAddress || null};
        if (editingSite) {
            const {data} = await axios.put(`/admin/sites/${editingSite.id}`, payload);
            setSites(prev => prev.map(s => s.id === data.id ? data : s));
            if (managingSite?.id === data.id) setManagingSite(data);
        } else {
            const {data} = await axios.post('/admin/sites', payload);
            setSites(prev => [...prev, data]);
        }
        closeSiteModal();
    }

    async function confirmDeleteSite() {
        if (!deleteSiteTarget) return;
        await axios.delete(`/admin/sites/${deleteSiteTarget.id}`);
        setSites(prev => prev.filter(s => s.id !== deleteSiteTarget.id));
        if (managingSite?.id === deleteSiteTarget.id) setManagingSite(null);
        setDeleteSiteTarget(null);
    }

    function openManageAssignments(site: EventSite) {
        setManagingSite(site);
    }

    function openCreateAssignment() {
        setEditingAssignment(null);
        setAssignment(emptyAssignment());
        setAssignmentModalOpen(true);
    }

    function openEditAssignment(a: SleepingAssignment) {
        setEditingAssignment(a);
        setAssignment({name: a.name, bunks: a.bunks, power: a.power, medical: a.medical, accessible: a.accessible, npcOnly: a.npcOnly, staffOnly: a.staffOnly});
        setAssignmentModalOpen(true);
    }

    function closeAssignmentModal() {
        setAssignmentModalOpen(false);
        setEditingAssignment(null);
        setAssignment(emptyAssignment());
    }

    async function saveAssignment() {
        if (!managingSite) return;
        if (editingAssignment) {
            const {data} = await axios.put(`/admin/sites/${managingSite.id}/assignments/${editingAssignment.id}`, assignment);
            const updated = {...managingSite, sleepingAssignments: (managingSite.sleepingAssignments ?? []).map(a => a.id === data.id ? data : a)};
            setManagingSite(updated);
            setSites(prev => prev.map(s => s.id === updated.id ? updated : s));
        } else {
            const {data} = await axios.post(`/admin/sites/${managingSite.id}/assignments`, assignment);
            const updated = {...managingSite, sleepingAssignments: [...(managingSite.sleepingAssignments ?? []), data]};
            setManagingSite(updated);
            setSites(prev => prev.map(s => s.id === updated.id ? updated : s));
        }
        closeAssignmentModal();
    }

    async function confirmDeleteAssignment() {
        if (!deleteAssignmentTarget || !managingSite) return;
        await axios.delete(`/admin/sites/${managingSite.id}/assignments/${deleteAssignmentTarget.id}`);
        const updated = {...managingSite, sleepingAssignments: (managingSite.sleepingAssignments ?? []).filter(a => a.id !== deleteAssignmentTarget.id)};
        setManagingSite(updated);
        setSites(prev => prev.map(s => s.id === updated.id ? updated : s));
        setDeleteAssignmentTarget(null);
    }

    const siteColumns: GridColDef[] = [
        {field: 'name', headerName: 'Site Name', flex: 2},
        {field: 'address', headerName: 'Address', flex: 3, valueFormatter: (p: any) => p.value ?? '—'},
        {
            field: 'assignments_count', headerName: 'Assignments', width: 120, align: 'center', headerAlign: 'center',
            valueGetter: (p: any) => p.row.sleepingAssignments?.length ?? 0,
        },
        {
            field: 'total_bunks', headerName: 'Total Bunks', width: 110, align: 'center', headerAlign: 'center',
            valueGetter: (p: any) => (p.row.sleepingAssignments ?? []).reduce((sum: number, a: any) => sum + (a.bunks ?? 0), 0),
        },
        {
            field: 'actions', type: 'actions', headerName: '', width: 160,
            getActions: (params) => {
                const site = params.row as EventSite;
                return [
                    <GridActionsCellItem icon={<BedIcon/>} label="Manage Assignments" onClick={() => openManageAssignments(site)}/>,
                    <GridActionsCellItem icon={<MapIcon/>} label="Upload Site Map" onClick={() => openMapUpload(site.id)}/>,
                    ...(site.siteMapKey ? [<GridActionsCellItem icon={<DeleteForeverIcon/>} label="Remove Site Map" onClick={() => deleteMap(site)}/>] : []),
                    <GridActionsCellItem icon={<EditIcon/>} label="Edit" onClick={() => openEditSite(site)}/>,
                    <GridActionsCellItem icon={<DeleteIcon/>} label="Delete" onClick={() => setDeleteSiteTarget(site)}/>,
                ];
            }
        }
    ];

    const assignmentColumns: GridColDef[] = [
        {field: 'name', headerName: 'Name', flex: 2},
        {field: 'bunks', headerName: 'Bunks', width: 70, align: 'center', headerAlign: 'center'},
        {field: 'power', headerName: 'Power', width: 70, align: 'center', headerAlign: 'center', type: 'boolean'},
        {field: 'medical', headerName: 'Medical', width: 80, align: 'center', headerAlign: 'center', type: 'boolean'},
        {field: 'accessible', headerName: 'ADA', width: 70, align: 'center', headerAlign: 'center', type: 'boolean'},
        {field: 'npcOnly', headerName: 'NPC Only', width: 90, align: 'center', headerAlign: 'center', type: 'boolean'},
        {field: 'staffOnly', headerName: 'Staff Only', width: 90, align: 'center', headerAlign: 'center', type: 'boolean'},
        {
            field: 'actions', type: 'actions', headerName: '', width: 80,
            getActions: (params) => [
                <GridActionsCellItem icon={<EditIcon/>} label="Edit" onClick={() => openEditAssignment(params.row as SleepingAssignment)}/>,
                <GridActionsCellItem icon={<DeleteIcon/>} label="Delete" onClick={() => setDeleteAssignmentTarget(params.row as SleepingAssignment)}/>,
            ]
        }
    ];

    return <React.Fragment>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">Event Site Management</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} md={managingSite ? 6 : 12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                        <Typography variant="h6">Sites</Typography>
                        <Button variant="contained" startIcon={<AddBoxIcon/>} onClick={openCreateSite}>New Site</Button>
                    </Box>
                    <DataGrid
                        autoHeight
                        rows={sites}
                        columns={siteColumns}
                        getRowId={r => r.id}
                        density="compact"
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>

            {managingSite && (
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{p: 1}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                            <Typography variant="h6">Sleeping Assignments — {managingSite.name}</Typography>
                            <Button variant="contained" startIcon={<AddBoxIcon/>} onClick={openCreateAssignment}>Add</Button>
                        </Box>
                        <DataGrid
                            autoHeight
                            rows={managingSite.sleepingAssignments ?? []}
                            columns={assignmentColumns}
                            getRowId={r => r.id}
                            density="compact"
                            initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                        />
                    </Paper>
                </Grid>
            )}
        </Grid>

        {/* Site create/edit modal */}
        <Modal open={siteModalOpen} onClose={closeSiteModal}>
            <Box sx={modalStyle}>
                <Typography variant="h6" align="center" gutterBottom>
                    {editingSite ? 'Edit Site' : 'New Site'}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Site Name" value={siteName} onChange={ev => setSiteName(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Address" value={siteAddress} onChange={ev => setSiteAddress(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={12} sx={{textAlign: 'center'}}>
                        <Button variant="contained" disabled={!siteName.trim()} onClick={saveSite}>
                            {editingSite ? 'Save' : 'Create'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>

        {/* Sleeping assignment create/edit modal */}
        <Modal open={assignmentModalOpen} onClose={closeAssignmentModal}>
            <Box sx={assignmentModalStyle}>
                <Typography variant="h6" align="center" gutterBottom>
                    {editingAssignment ? 'Edit Assignment' : 'New Assignment'}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Name" value={assignment.name}
                                   onChange={ev => setAssignment(p => ({...p, name: ev.target.value}))}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Number of Bunks" type="number" value={assignment.bunks}
                                   inputProps={{min: 0}}
                                   onChange={ev => setAssignment(p => ({...p, bunks: parseInt(ev.target.value) || 0}))}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel control={<Checkbox checked={assignment.power} onChange={ev => setAssignment(p => ({...p, power: ev.target.checked}))}/>} label="Power"/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel control={<Checkbox checked={assignment.medical} onChange={ev => setAssignment(p => ({...p, medical: ev.target.checked}))}/>} label="Medical"/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel control={<Checkbox checked={assignment.accessible} onChange={ev => setAssignment(p => ({...p, accessible: ev.target.checked}))}/>} label="ADA Accessible"/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel control={<Checkbox checked={assignment.npcOnly} onChange={ev => setAssignment(p => ({...p, npcOnly: ev.target.checked}))}/>} label="NPC Only"/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel control={<Checkbox checked={assignment.staffOnly} onChange={ev => setAssignment(p => ({...p, staffOnly: ev.target.checked}))}/>} label="Staff Only"/>
                    </Grid>
                    <Grid item xs={12} sx={{textAlign: 'center'}}>
                        <Button variant="contained" disabled={!assignment.name.trim()} onClick={saveAssignment}>
                            {editingAssignment ? 'Save' : 'Add'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>

        {/* Delete site confirmation */}
        <Dialog open={!!deleteSiteTarget} onClose={() => setDeleteSiteTarget(null)}>
            <DialogTitle>Delete Site</DialogTitle>
            <DialogContent>
                Are you sure you want to delete "{deleteSiteTarget?.name}"? All sleeping assignments for this site will also be deleted. Events assigned to this site will be unlinked.
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteSiteTarget(null)}>Cancel</Button>
                <Button color="error" onClick={confirmDeleteSite}>Delete</Button>
            </DialogActions>
        </Dialog>

        {/* Delete assignment confirmation */}
        <Dialog open={!!deleteAssignmentTarget} onClose={() => setDeleteAssignmentTarget(null)}>
            <DialogTitle>Delete Assignment</DialogTitle>
            <DialogContent>
                Are you sure you want to delete "{deleteAssignmentTarget?.name}"?
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteAssignmentTarget(null)}>Cancel</Button>
                <Button color="error" onClick={confirmDeleteAssignment}>Delete</Button>
            </DialogActions>
        </Dialog>

        <input ref={mapFileInputRef} type="file" accept="image/*" style={{display: 'none'}} onChange={handleMapFileChange}/>
    </React.Fragment>
}
